import { SKILLS, KEYSTONES, ITEMS, CONSUMABLES, aspectOf } from '@knight/shared';
import { RES } from './art';

/**
 * Procedural icons for skills, keystones, items and consumables: a dark plate with the
 * content colour glowing behind an ivory symbol. Drawn once per id and cached as data URLs.
 */
type Ctx = CanvasRenderingContext2D;
type Painter = (ctx: Ctx, c: string) => void;

const SIZE = 56;
const CX = 28;
const CY = 28;
const cache = new Map<string, string>();

// ------------------------------------------------------------------ colour helpers

function rgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  const v = h.length === 3 ? h.split('').map((c) => parseInt(c + c, 16)) : [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
  return [v[0] || 0, v[1] || 0, v[2] || 0];
}

function mix(hex: string, to: [number, number, number], k: number): string {
  const [r, g, b] = rgb(hex);
  const m = (a: number, t: number) => Math.round(a + (t - a) * k);
  return `rgb(${m(r, to[0])},${m(g, to[1])},${m(b, to[2])})`;
}

const lighten = (hex: string, k: number) => mix(hex, [255, 250, 240], k);
const darken = (hex: string, k: number) => mix(hex, [10, 8, 14], k);
function alpha(hex: string, a: number): string {
  const [r, g, b] = rgb(hex);
  return `rgba(${r},${g},${b},${a})`;
}

// ------------------------------------------------------------------ drawing helpers

function plate(ctx: Ctx, c: string): void {
  rr(ctx, 1, 1, SIZE - 2, SIZE - 2, 9);
  const g = ctx.createLinearGradient(0, 0, 0, SIZE);
  g.addColorStop(0, '#241c31');
  g.addColorStop(1, '#0d0a12');
  ctx.fillStyle = g;
  ctx.fill();
  const rg = ctx.createRadialGradient(CX, CY - 3, 2, CX, CY - 1, 30);
  rg.addColorStop(0, alpha(c, 0.7));
  rg.addColorStop(0.55, alpha(c, 0.22));
  rg.addColorStop(1, alpha(c, 0));
  ctx.fillStyle = rg;
  ctx.fill();
  const sg = ctx.createLinearGradient(0, 30, 0, SIZE);
  sg.addColorStop(0, 'rgba(0,0,0,0)');
  sg.addColorStop(1, 'rgba(0,0,0,0.5)');
  ctx.fillStyle = sg;
  ctx.fill();
  rr(ctx, 2.5, 2.5, SIZE - 5, SIZE - 5, 8);
  ctx.strokeStyle = 'rgba(255,255,255,0.09)';
  ctx.lineWidth = 1;
  ctx.stroke();
}

function rr(ctx: Ctx, x: number, y: number, w: number, h: number, r: number): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

/** Fills the current path: dark outline, glow, light-to-colour gradient, faint rim. */
function fill(ctx: Ctx, c: string, path: () => void, light = 0.7, y0 = 8, y1 = 50): void {
  ctx.save();
  path();
  ctx.lineJoin = 'round';
  ctx.lineWidth = 3;
  ctx.strokeStyle = 'rgba(8,6,12,0.92)';
  ctx.stroke();
  ctx.shadowColor = c;
  ctx.shadowBlur = 5 * RES;
  const g = ctx.createLinearGradient(0, y0, 0, y1);
  g.addColorStop(0, lighten(c, light));
  g.addColorStop(1, c);
  ctx.fillStyle = g;
  ctx.fill();
  ctx.shadowBlur = 0;
  ctx.lineWidth = 1;
  ctx.strokeStyle = 'rgba(255,255,255,0.28)';
  ctx.stroke();
  ctx.restore();
}

/** Strokes the current path with a dark halo and a light colour. */
function stroke(ctx: Ctx, c: string, width: number, path: () => void, light = 0.5): void {
  ctx.save();
  path();
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.lineWidth = width + 2.6;
  ctx.strokeStyle = 'rgba(8,6,12,0.9)';
  ctx.stroke();
  ctx.shadowColor = c;
  ctx.shadowBlur = 5 * RES;
  ctx.lineWidth = width;
  ctx.strokeStyle = lighten(c, light);
  ctx.stroke();
  ctx.restore();
}

/** Dark fill for holes and details on top of a symbol. */
function dark(ctx: Ctx, path: () => void, color = '#0e0b14'): void {
  ctx.save();
  path();
  ctx.fillStyle = color;
  ctx.fill();
  ctx.restore();
}

function light(ctx: Ctx, path: () => void, a = 0.85): void {
  ctx.save();
  path();
  ctx.fillStyle = `rgba(255,250,240,${a})`;
  ctx.fill();
  ctx.restore();
}

// ------------------------------------------------------------------ path primitives (all start a new path)

const P = {
  circle(ctx: Ctx, x: number, y: number, r: number) {
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
  },
  poly(ctx: Ctx, pts: [number, number][]) {
    ctx.beginPath();
    pts.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y)));
    ctx.closePath();
  },
  line(ctx: Ctx, pts: [number, number][]) {
    ctx.beginPath();
    pts.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y)));
  },
  flame(ctx: Ctx, x: number, y: number, s: number) {
    ctx.beginPath();
    ctx.moveTo(x, y - s);
    ctx.bezierCurveTo(x + s * 0.1, y - s * 0.45, x + s * 0.8, y - s * 0.35, x + s * 0.62, y + s * 0.3);
    ctx.bezierCurveTo(x + s * 0.5, y + s * 0.75, x - s * 0.5, y + s * 0.75, x - s * 0.62, y + s * 0.3);
    ctx.bezierCurveTo(x - s * 0.8, y - s * 0.15, x - s * 0.15, y - s * 0.4, x, y - s);
    ctx.closePath();
  },
  drop(ctx: Ctx, x: number, y: number, s: number) {
    ctx.beginPath();
    ctx.moveTo(x, y - s);
    ctx.bezierCurveTo(x + s * 0.12, y - s * 0.45, x + s * 0.62, y - s * 0.1, x + s * 0.62, y + s * 0.3);
    ctx.arc(x, y + s * 0.3, s * 0.62, 0, Math.PI, false);
    ctx.bezierCurveTo(x - s * 0.62, y - s * 0.1, x - s * 0.12, y - s * 0.45, x, y - s);
    ctx.closePath();
  },
  star(ctx: Ctx, x: number, y: number, r: number, n: number, inner: number, rot = 0) {
    ctx.beginPath();
    for (let i = 0; i < n * 2; i++) {
      const a = -Math.PI / 2 + rot + (i * Math.PI) / n;
      const rad = i % 2 === 0 ? r : r * inner;
      const px = x + Math.cos(a) * rad;
      const py = y + Math.sin(a) * rad;
      if (i === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    }
    ctx.closePath();
  },
  bolt(ctx: Ctx, x: number, y: number, s: number) {
    P.poly(ctx, [
      [x + s * 0.2, y - s],
      [x - s * 0.5, y + s * 0.12],
      [x - s * 0.02, y + s * 0.12],
      [x - s * 0.3, y + s],
      [x + s * 0.55, y - s * 0.18],
      [x + s * 0.08, y - s * 0.18],
    ]);
  },
  heart(ctx: Ctx, x: number, y: number, s: number) {
    ctx.beginPath();
    ctx.moveTo(x, y + s * 0.95);
    ctx.bezierCurveTo(x - s * 1.25, y + s * 0.05, x - s * 0.7, y - s * 0.95, x, y - s * 0.4);
    ctx.bezierCurveTo(x + s * 0.7, y - s * 0.95, x + s * 1.25, y + s * 0.05, x, y + s * 0.95);
    ctx.closePath();
  },
  shield(ctx: Ctx, x: number, y: number, s: number) {
    ctx.beginPath();
    ctx.moveTo(x - s * 0.78, y - s * 0.8);
    ctx.lineTo(x + s * 0.78, y - s * 0.8);
    ctx.quadraticCurveTo(x + s * 0.85, y + s * 0.3, x, y + s);
    ctx.quadraticCurveTo(x - s * 0.85, y + s * 0.3, x - s * 0.78, y - s * 0.8);
    ctx.closePath();
  },
  blade(ctx: Ctx, x: number, y: number, len: number, w: number) {
    P.poly(ctx, [
      [x, y - len],
      [x + w, y - len + w * 1.8],
      [x + w, y + len * 0.3],
      [x - w, y + len * 0.3],
      [x - w, y - len + w * 1.8],
    ]);
  },
  crescent(ctx: Ctx, x: number, y: number, r: number) {
    ctx.beginPath();
    ctx.arc(x, y, r, -Math.PI * 0.5, Math.PI * 0.5);
    ctx.arc(x + r * 0.5, y, r * 0.78, Math.PI * 0.5, -Math.PI * 0.5, true);
    ctx.closePath();
  },
  skull(ctx: Ctx, x: number, y: number, s: number) {
    ctx.beginPath();
    ctx.arc(x, y - s * 0.12, s * 0.86, Math.PI * 0.92, Math.PI * 2.08);
    ctx.lineTo(x + s * 0.5, y + s * 0.8);
    ctx.lineTo(x - s * 0.5, y + s * 0.8);
    ctx.closePath();
  },
  skullDetails(ctx: Ctx, x: number, y: number, s: number) {
    dark(ctx, () => P.circle(ctx, x - s * 0.34, y - s * 0.12, s * 0.24));
    dark(ctx, () => P.circle(ctx, x + s * 0.34, y - s * 0.12, s * 0.24));
    dark(ctx, () => P.poly(ctx, [[x, y + s * 0.15], [x - s * 0.12, y + s * 0.4], [x + s * 0.12, y + s * 0.4]]));
    dark(ctx, () => P.line(ctx, [[x - s * 0.3, y + s * 0.55], [x - s * 0.3, y + s * 0.8]]));
    ctx.save();
    ctx.strokeStyle = '#0e0b14';
    ctx.lineWidth = 1.2;
    for (const dx of [-0.28, -0.09, 0.09, 0.28]) {
      ctx.beginPath();
      ctx.moveTo(x + dx * s, y + s * 0.55);
      ctx.lineTo(x + dx * s, y + s * 0.8);
      ctx.stroke();
    }
    ctx.restore();
  },
  eye(ctx: Ctx, x: number, y: number, s: number) {
    ctx.beginPath();
    ctx.moveTo(x - s, y);
    ctx.quadraticCurveTo(x, y - s * 0.95, x + s, y);
    ctx.quadraticCurveTo(x, y + s * 0.95, x - s, y);
    ctx.closePath();
  },
  flask(ctx: Ctx, x: number, y: number, s: number) {
    ctx.beginPath();
    ctx.moveTo(x - s * 0.3, y - s);
    ctx.lineTo(x + s * 0.3, y - s);
    ctx.lineTo(x + s * 0.3, y - s * 0.35);
    ctx.arc(x, y + s * 0.25, s * 0.75, -Math.PI * 0.32, Math.PI * 1.32);
    ctx.lineTo(x - s * 0.3, y - s * 0.35);
    ctx.closePath();
  },
  cloud(ctx: Ctx, x: number, y: number, s: number) {
    ctx.beginPath();
    ctx.arc(x - s * 0.55, y + s * 0.15, s * 0.45, Math.PI * 0.5, Math.PI * 1.5);
    ctx.arc(x - s * 0.15, y - s * 0.3, s * 0.5, Math.PI * 1.05, Math.PI * 1.85);
    ctx.arc(x + s * 0.4, y - s * 0.1, s * 0.5, Math.PI * 1.25, Math.PI * 0.35);
    ctx.arc(x + s * 0.5, y + s * 0.3, s * 0.32, Math.PI * 1.6, Math.PI * 0.5);
    ctx.closePath();
  },
  crown(ctx: Ctx, x: number, y: number, s: number) {
    P.poly(ctx, [
      [x - s, y + s * 0.6],
      [x - s, y - s * 0.3],
      [x - s * 0.5, y + s * 0.15],
      [x, y - s * 0.85],
      [x + s * 0.5, y + s * 0.15],
      [x + s, y - s * 0.3],
      [x + s, y + s * 0.6],
    ]);
  },
  bone(ctx: Ctx, x: number, y: number, s: number, rot: number) {
    ctx.beginPath();
    const c = Math.cos(rot);
    const sn = Math.sin(rot);
    const pt = (px: number, py: number): [number, number] => [x + px * c - py * sn, y + px * sn + py * c];
    const w = s * 0.2;
    const [a1, a2] = [pt(-s, -w), pt(s, -w)];
    const [b1, b2] = [pt(s, w), pt(-s, w)];
    ctx.moveTo(a1[0], a1[1]);
    ctx.lineTo(a2[0], a2[1]);
    ctx.lineTo(b1[0], b1[1]);
    ctx.lineTo(b2[0], b2[1]);
    ctx.closePath();
    for (const [kx, ky] of [
      [-s, -w * 1.1],
      [-s, w * 1.1],
      [s, -w * 1.1],
      [s, w * 1.1],
    ]) {
      const [px, py] = pt(kx, ky);
      ctx.moveTo(px + s * 0.28, py);
      ctx.arc(px, py, s * 0.28, 0, Math.PI * 2);
    }
  },
  wing(ctx: Ctx, x: number, y: number, s: number, dir: number) {
    ctx.beginPath();
    ctx.moveTo(x, y + s * 0.6);
    ctx.quadraticCurveTo(x + dir * s * 0.15, y - s * 0.95, x + dir * s * 1.15, y - s * 0.75);
    ctx.quadraticCurveTo(x + dir * s * 0.7, y - s * 0.35, x + dir * s * 0.95, y - s * 0.1);
    ctx.quadraticCurveTo(x + dir * s * 0.5, y, x + dir * s * 0.7, y + s * 0.3);
    ctx.quadraticCurveTo(x + dir * s * 0.3, y + s * 0.3, x, y + s * 0.6);
    ctx.closePath();
  },
  horn(ctx: Ctx, x: number, y: number, s: number) {
    ctx.beginPath();
    ctx.moveTo(x - s * 0.95, y + s * 0.55);
    ctx.quadraticCurveTo(x - s * 0.3, y - s * 0.15, x + s * 0.7, y - s * 0.75);
    ctx.lineTo(x + s * 0.98, y - s * 0.3);
    ctx.quadraticCurveTo(x + s * 0.1, y + s * 0.15, x - s * 0.5, y + s * 0.9);
    ctx.closePath();
  },
  torso(ctx: Ctx, x: number, y: number, s: number) {
    ctx.beginPath();
    ctx.moveTo(x - s * 0.95, y - s * 0.65);
    ctx.lineTo(x - s * 0.45, y - s * 0.95);
    ctx.quadraticCurveTo(x, y - s * 0.55, x + s * 0.45, y - s * 0.95);
    ctx.lineTo(x + s * 0.95, y - s * 0.65);
    ctx.lineTo(x + s * 0.78, y - s * 0.05);
    ctx.lineTo(x + s * 0.62, y + s * 0.95);
    ctx.lineTo(x - s * 0.62, y + s * 0.95);
    ctx.lineTo(x - s * 0.78, y - s * 0.05);
    ctx.closePath();
  },
  gem(ctx: Ctx, x: number, y: number, s: number) {
    P.poly(ctx, [
      [x - s, y - s * 0.2],
      [x - s * 0.5, y - s * 0.8],
      [x + s * 0.5, y - s * 0.8],
      [x + s, y - s * 0.2],
      [x, y + s],
    ]);
  },
  figure(ctx: Ctx, x: number, y: number, s: number) {
    ctx.beginPath();
    ctx.moveTo(x + s * 0.36, y - s * 0.5);
    ctx.arc(x, y - s * 0.5, s * 0.36, 0, Math.PI * 2);
    ctx.moveTo(x - s * 0.62, y + s);
    ctx.quadraticCurveTo(x - s * 0.62, y - s * 0.05, x, y - s * 0.05);
    ctx.quadraticCurveTo(x + s * 0.62, y - s * 0.05, x + s * 0.62, y + s);
    ctx.closePath();
  },
  spiral(ctx: Ctx, x: number, y: number, r: number, turns: number) {
    ctx.beginPath();
    const steps = 90;
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      const a = t * turns * Math.PI * 2;
      const rad = 1.5 + t * r;
      const px = x + Math.cos(a) * rad;
      const py = y + Math.sin(a) * rad * 0.9;
      if (i === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    }
  },
};

function arrow(ctx: Ctx, c: string, x0: number, y0: number, x1: number, y1: number, w = 2.4): void {
  const a = Math.atan2(y1 - y0, x1 - x0);
  const hl = 7;
  stroke(ctx, c, w, () => P.line(ctx, [[x0, y0], [x1 - Math.cos(a) * hl * 0.6, y1 - Math.sin(a) * hl * 0.6]]), 0.35);
  fill(ctx, c, () =>
    P.poly(ctx, [
      [x1, y1],
      [x1 - Math.cos(a - 0.5) * hl, y1 - Math.sin(a - 0.5) * hl],
      [x1 - Math.cos(a + 0.5) * hl, y1 - Math.sin(a + 0.5) * hl],
    ]),
  );
  // fletching
  stroke(ctx, c, 1.6, () => P.line(ctx, [[x0 + Math.cos(a + 2.4) * 4, y0 + Math.sin(a + 2.4) * 4], [x0, y0], [x0 + Math.cos(a - 2.4) * 4, y0 + Math.sin(a - 2.4) * 4]]), 0.35);
}

function snowflake(ctx: Ctx, c: string, x: number, y: number, r: number, w = 2.2): void {
  stroke(ctx, c, w, () => {
    ctx.beginPath();
    for (let i = 0; i < 6; i++) {
      const a = (i * Math.PI) / 3;
      const ex = x + Math.cos(a) * r;
      const ey = y + Math.sin(a) * r;
      ctx.moveTo(x, y);
      ctx.lineTo(ex, ey);
      const bx = x + Math.cos(a) * r * 0.62;
      const by = y + Math.sin(a) * r * 0.62;
      for (const d of [-0.6, 0.6]) {
        ctx.moveTo(bx, by);
        ctx.lineTo(bx + Math.cos(a + d) * r * 0.3, by + Math.sin(a + d) * r * 0.3);
      }
    }
  });
}

function sword(ctx: Ctx, c: string, x: number, y: number, s: number, rot = 0, dark_ = false): void {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(rot);
  fill(ctx, dark_ ? darken(c, 0.2) : lighten(c, 0.15), () => P.blade(ctx, 0, 0, s, s * 0.17), dark_ ? 0.25 : 0.75, -s, s * 0.3);
  fill(ctx, '#b08a4a', () => P.poly(ctx, [[-s * 0.48, s * 0.3], [s * 0.48, s * 0.3], [s * 0.48, s * 0.44], [-s * 0.48, s * 0.44]]), 0.35);
  fill(ctx, '#5a3f2a', () => P.poly(ctx, [[-s * 0.1, s * 0.44], [s * 0.1, s * 0.44], [s * 0.1, s * 0.85], [-s * 0.1, s * 0.85]]), 0.25);
  fill(ctx, '#b08a4a', () => P.circle(ctx, 0, s * 0.93, s * 0.13), 0.4);
  // edge highlight
  light(ctx, () => P.poly(ctx, [[0, -s], [s * 0.05, -s * 0.8], [s * 0.05, s * 0.25], [0, s * 0.25]]), 0.35);
  ctx.restore();
}

function bow(ctx: Ctx, c: string, x: number, y: number, s: number, rot = 0): void {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(rot);
  stroke(ctx, '#0e0b14', 1.4, () => P.line(ctx, [[-s * 0.1, -s], [-s * 0.1, s]]), 0.9);
  stroke(ctx, c, 3, () => {
    ctx.beginPath();
    ctx.moveTo(-s * 0.1, -s);
    ctx.quadraticCurveTo(s * 0.95, 0, -s * 0.1, s);
  });
  ctx.restore();
}

function staff(ctx: Ctx, c: string, x0: number, y0: number, x1: number, y1: number, orb: number): void {
  stroke(ctx, '#6a4a2c', 3.2, () => P.line(ctx, [[x0, y0], [x1, y1]]), 0.3);
  if (orb > 0) fill(ctx, c, () => P.circle(ctx, x1, y1, orb), 0.8);
}

function rays(ctx: Ctx, c: string, x: number, y: number, r0: number, r1: number, n: number, w = 2, rot = 0): void {
  stroke(ctx, c, w, () => {
    ctx.beginPath();
    for (let i = 0; i < n; i++) {
      const a = rot + (i * Math.PI * 2) / n;
      ctx.moveTo(x + Math.cos(a) * r0, y + Math.sin(a) * r0);
      ctx.lineTo(x + Math.cos(a) * r1, y + Math.sin(a) * r1);
    }
  });
}

function diamondFrame(ctx: Ctx, c: string): void {
  ctx.save();
  P.poly(ctx, [[CX, 4], [52, CY], [CX, 52], [4, CY]]);
  ctx.strokeStyle = alpha(c, 0.55);
  ctx.lineWidth = 1.5;
  ctx.stroke();
  ctx.restore();
}

function pendant(ctx: Ctx, c: string, inner: (x: number, y: number) => void): void {
  stroke(ctx, '#b08a4a', 1.8, () => {
    ctx.beginPath();
    ctx.moveTo(10, 6);
    ctx.quadraticCurveTo(CX, 28, 46, 6);
  }, 0.3);
  fill(ctx, '#b08a4a', () => P.circle(ctx, CX, 22, 3), 0.4);
  fill(ctx, c, () => P.gem(ctx, CX, 36, 12), 0.55, 24, 48);
  inner(CX, 34);
}

// ------------------------------------------------------------------ catalogue

const SKILL_ICONS: Record<string, Painter> = {
  wail: (ctx, c) => {
    fill(ctx, c, () => P.skull(ctx, CX, 24, 12));
    P.skullDetails(ctx, CX, 24, 12);
    stroke(ctx, c, 2, () => {
      ctx.beginPath();
      ctx.moveTo(14, 44);
      ctx.quadraticCurveTo(21, 38, 28, 44);
      ctx.quadraticCurveTo(35, 50, 42, 44);
    });
  },
  light_volley: (ctx, c) => {
    arrow(ctx, c, 15, 46, 21, 12);
    arrow(ctx, c, 28, 48, 28, 10);
    arrow(ctx, c, 41, 46, 35, 12);
  },
  hex: (ctx, c) => {
    stroke(ctx, c, 1.4, () => P.circle(ctx, CX, CY, 21), 0.2);
    rays(ctx, c, CX, CY, 19, 23, 4, 1.6, Math.PI / 4);
    fill(ctx, c, () => P.eye(ctx, CX, CY, 15));
    dark(ctx, () => P.circle(ctx, CX, CY, 5.5));
    light(ctx, () => P.circle(ctx, CX + 2, CY - 2, 1.6));
  },
  fireball: (ctx, c) => {
    fill(ctx, c, () => P.flame(ctx, CX, 30, 18), 0.75);
    light(ctx, () => P.flame(ctx, CX, 36, 7), 0.75);
  },
  frost_nova: (ctx, c) => {
    snowflake(ctx, c, CX, CY, 19);
    fill(ctx, c, () => P.circle(ctx, CX, CY, 3.2), 0.9);
  },
  chain_lightning: (ctx, c) => fill(ctx, c, () => P.bolt(ctx, CX, CY, 19), 0.85),
  shield_bash: (ctx, c) => {
    fill(ctx, c, () => P.shield(ctx, 24, 29, 15));
    rays(ctx, c, 40, 22, 6, 13, 3, 2.2, -0.9);
  },
  blood_rite: (ctx, c) => {
    stroke(ctx, '#cfd8e6', 2.2, () => P.line(ctx, [[12, 44], [44, 12]]), 0.5);
    fill(ctx, c, () => P.drop(ctx, CX, 27, 16));
    light(ctx, () => P.circle(ctx, CX - 4, 31, 2), 0.5);
  },
  holy_ward: (ctx, c) => {
    rays(ctx, c, CX, CY, 17, 23, 8, 2);
    stroke(ctx, c, 4, () => P.circle(ctx, CX, CY, 11), 0.7);
  },
  void_rift: (ctx, c) => {
    stroke(ctx, c, 3.2, () => P.spiral(ctx, CX, CY, 19, 2.6), 0.45);
    dark(ctx, () => P.circle(ctx, CX, CY, 3.5));
  },
  arrow_storm: (ctx, c) => {
    fill(ctx, c, () => P.cloud(ctx, CX, 19, 11), 0.65);
    arrow(ctx, c, 17, 30, 15, 47, 1.8);
    arrow(ctx, c, 28, 32, 28, 49, 1.8);
    arrow(ctx, c, 39, 30, 41, 47, 1.8);
  },
  war_cry: (ctx, c) => {
    fill(ctx, c, () => P.horn(ctx, 24, 31, 15));
    stroke(ctx, c, 2, () => {
      ctx.beginPath();
      ctx.arc(36, 20, 8, -1.2, 0.6);
      ctx.moveTo(41, 12);
      ctx.arc(36, 20, 14, -1.1, 0.5);
    });
  },
  orbit_blades: (ctx, c) => {
    stroke(ctx, c, 1.4, () => P.circle(ctx, CX, CY, 14), 0.2);
    for (let i = 0; i < 3; i++) {
      const a = -Math.PI / 2 + (i * Math.PI * 2) / 3;
      const x = CX + Math.cos(a) * 14;
      const y = CY + Math.sin(a) * 14;
      fill(ctx, c, () => P.poly(ctx, [[x + Math.cos(a) * 8, y + Math.sin(a) * 8], [x + Math.cos(a + 2.1) * 5, y + Math.sin(a + 2.1) * 5], [x + Math.cos(a - 2.1) * 5, y + Math.sin(a - 2.1) * 5]]));
    }
    fill(ctx, c, () => P.circle(ctx, CX, CY, 3), 0.9);
  },
  ember_aura: (ctx, c) => {
    for (let i = 0; i < 6; i++) {
      const a = -Math.PI / 2 + (i * Math.PI * 2) / 6;
      fill(ctx, c, () => P.flame(ctx, CX + Math.cos(a) * 16, CY + 2 + Math.sin(a) * 16, 6.5), 0.7);
    }
    fill(ctx, c, () => P.circle(ctx, CX, CY, 5), 0.9);
  },
  frost_pulse: (ctx, c) => {
    stroke(ctx, c, 1.5, () => P.circle(ctx, CX, CY, 21), 0.15);
    stroke(ctx, c, 2.2, () => P.circle(ctx, CX, CY, 14), 0.4);
    snowflake(ctx, c, CX, CY, 7, 1.6);
  },
  spirit_bolt: (ctx, c) => {
    stroke(ctx, c, 5, () => {
      ctx.beginPath();
      ctx.moveTo(35, 22);
      ctx.quadraticCurveTo(22, 30, 12, 44);
    }, 0.3);
    fill(ctx, c, () => P.circle(ctx, 36, 21, 8), 0.85);
    light(ctx, () => P.circle(ctx, 38, 19, 2.5), 0.8);
  },
  bone_shards: (ctx, c) => {
    fill(ctx, c, () => P.bone(ctx, 20, 34, 9, -0.7), 0.55);
    fill(ctx, c, () => P.bone(ctx, 34, 22, 9, 0.35), 0.55);
    fill(ctx, c, () => P.bone(ctx, 32, 40, 7, 1.15), 0.55);
  },
  blight_cloud: (ctx, c) => {
    fill(ctx, c, () => P.cloud(ctx, CX, 27, 14), 0.6);
    for (const [x, y, r] of [
      [20, 26, 2.6],
      [30, 22, 2],
      [36, 30, 2.4],
    ])
      dark(ctx, () => P.circle(ctx, x, y, r), alpha(c, 0.5));
    fill(ctx, c, () => P.skull(ctx, CX, 44, 5), 0.8);
  },
  guardian_totem: (ctx, c) => {
    fill(ctx, '#7a5a3a', () => P.poly(ctx, [[19, 14], [37, 14], [35, 48], [21, 48]]), 0.3);
    fill(ctx, c, () => P.poly(ctx, [[15, 8], [41, 8], [39, 15], [17, 15]]), 0.6);
    dark(ctx, () => P.circle(ctx, 23, 24, 2.4));
    dark(ctx, () => P.circle(ctx, 33, 24, 2.4));
    stroke(ctx, c, 1.8, () => P.line(ctx, [[22, 34], [34, 34]]), 0.6);
    stroke(ctx, c, 1.8, () => P.line(ctx, [[23, 40], [33, 40]]), 0.6);
  },
  mirror_shards: (ctx, c) => {
    fill(ctx, c, () => P.gem(ctx, 20, 30, 8), 0.8);
    fill(ctx, c, () => P.gem(ctx, 35, 24, 9), 0.8);
    fill(ctx, c, () => P.gem(ctx, 31, 42, 6), 0.8);
  },
  // signatures of the unlockable heroes
  bulwark_oath: (ctx, c) => {
    stroke(ctx, c, 1.6, () => P.circle(ctx, CX, CY, 22), 0.25);
    rays(ctx, c, CX, CY, 20, 24, 8, 1.6, Math.PI / 8);
    fill(ctx, c, () => P.shield(ctx, CX, CY, 14), 0.6);
    light(ctx, () => P.poly(ctx, [[CX, 20], [CX + 4, 27], [CX, 34], [CX - 4, 27]]), 0.6);
  },
  spirit_pact: (ctx, c) => {
    fill(ctx, c, () => P.poly(ctx, [[18, 22], [12, 6], [24, 16]]), 0.5);
    fill(ctx, c, () => P.poly(ctx, [[38, 22], [44, 6], [32, 16]]), 0.5);
    fill(ctx, c, () => P.circle(ctx, CX, 28, 13), 0.65);
    dark(ctx, () => P.poly(ctx, [[20, 24], [26, 27], [20, 30]]));
    dark(ctx, () => P.poly(ctx, [[36, 24], [30, 27], [36, 30]]));
    dark(ctx, () => P.poly(ctx, [[22, 35], [34, 35], [28, 39]]));
  },
  thornroot: (ctx, c) => {
    stroke(ctx, c, 3.2, () => {
      ctx.beginPath();
      ctx.moveTo(CX, 50);
      ctx.quadraticCurveTo(22, 34, 30, 18);
      ctx.quadraticCurveTo(34, 10, 30, 6);
    }, 0.4);
    for (const [x, y, a] of [
      [25, 40, -0.9],
      [31, 30, 0.6],
      [26, 22, -1.1],
      [33, 14, 0.9],
    ])
      fill(ctx, c, () => P.poly(ctx, [[x, y], [x + Math.cos(a) * 9, y + Math.sin(a) * 9 - 4], [x + Math.cos(a) * 2, y + Math.sin(a) * 2 + 3]]), 0.7);
    fill(ctx, c, () => P.drop(ctx, 18, 30, 6), 0.7);
    fill(ctx, c, () => P.drop(ctx, 40, 24, 6), 0.7);
  },
  tiger_form: (ctx, c) => {
    fill(ctx, c, () => P.poly(ctx, [[16, 20], [12, 6], [24, 14]]), 0.6);
    fill(ctx, c, () => P.poly(ctx, [[40, 20], [44, 6], [32, 14]]), 0.6);
    fill(ctx, c, () => P.circle(ctx, CX, 28, 15), 0.7);
    for (const [x0, y0, x1, y1] of [
      [18, 18, 22, 24],
      [25, 14, 26, 21],
      [31, 14, 30, 21],
      [38, 18, 34, 24],
    ])
      stroke(ctx, '#0e0b14', 2, () => P.line(ctx, [[x0, y0], [x1, y1]]), 0);
    dark(ctx, () => P.eye(ctx, 22, 28, 3.5));
    dark(ctx, () => P.eye(ctx, 34, 28, 3.5));
    light(ctx, () => P.circle(ctx, 22, 28, 1.2), 0.9);
    light(ctx, () => P.circle(ctx, 34, 28, 1.2), 0.9);
    dark(ctx, () => P.poly(ctx, [[25, 35], [31, 35], [28, 38]]));
  },
  kindred_beast: (ctx, c) => {
    fill(ctx, c, () => P.circle(ctx, 17, 17, 6), 0.5);
    fill(ctx, c, () => P.circle(ctx, 39, 17, 6), 0.5);
    fill(ctx, c, () => P.circle(ctx, CX, 29, 15), 0.55);
    fill(ctx, c, () => P.circle(ctx, CX, 35, 7), 0.85);
    dark(ctx, () => P.circle(ctx, 22, 26, 2.2));
    dark(ctx, () => P.circle(ctx, 34, 26, 2.2));
    dark(ctx, () => P.circle(ctx, CX, 34, 2.4));
  },
  walking_tree: (ctx, c) => {
    fill(ctx, '#6a4a2c', () => P.poly(ctx, [[24, 50], [32, 50], [31, 30], [25, 30]]), 0.3);
    fill(ctx, '#6a4a2c', () => P.poly(ctx, [[20, 50], [25, 50], [26, 42], [22, 40]]), 0.3);
    fill(ctx, '#6a4a2c', () => P.poly(ctx, [[31, 50], [36, 50], [34, 40], [30, 42]]), 0.3);
    fill(ctx, c, () => P.circle(ctx, CX, 20, 13), 0.5);
    fill(ctx, c, () => P.circle(ctx, 17, 26, 8), 0.5);
    fill(ctx, c, () => P.circle(ctx, 39, 26, 8), 0.5);
    dark(ctx, () => P.eye(ctx, 24, 32, 2.2));
    dark(ctx, () => P.eye(ctx, 32, 32, 2.2));
  },
  vine: (ctx, c) => {
    stroke(ctx, c, 3.2, () => {
      ctx.beginPath();
      ctx.moveTo(14, 50);
      ctx.bezierCurveTo(10, 30, 40, 36, 30, 20);
      ctx.bezierCurveTo(24, 10, 40, 6, 44, 10);
    }, 0.6);
    for (const [x, y] of [[18, 40], [30, 30], [26, 16], [40, 12]] as [number, number][]) fill(ctx, c, () => P.circle(ctx, x, y, 4), 0.8);
  },
  flytrap: (ctx, c) => {
    fill(ctx, '#4a6a2a', () => P.poly(ctx, [[26, 50], [30, 50], [30, 34], [26, 34]]), 0.3);
    fill(ctx, c, () => {
      ctx.beginPath();
      ctx.ellipse(CX, 24, 15, 10, -0.3, 0, Math.PI * 2);
    }, 0.55);
    for (let i = 0; i < 6; i++) fill(ctx, '#ffe3f0', () => P.poly(ctx, [[15 + i * 5, 22], [17 + i * 5, 14], [19 + i * 5, 22]]), 0.7);
    dark(ctx, () => P.poly(ctx, [[15, 25], [41, 23], [30, 30]]), alpha('#000000', 0.5));
  },
  attunement: (ctx, c) => {
    stroke(ctx, c, 1.4, () => P.circle(ctx, CX, CY, 20), 0.2);
    fill(ctx, '#ff7a2f', () => P.flame(ctx, CX, 15, 8), 0.7);
    snowflake(ctx, '#7fdcff', 15, 36, 7, 1.6);
    fill(ctx, '#a8f0ff', () => P.bolt(ctx, 41, 36, 8), 0.9);
    fill(ctx, c, () => P.circle(ctx, CX, CY + 2, 3), 0.9);
  },
  turret: (ctx, c) => {
    fill(ctx, '#3a3542', () => P.poly(ctx, [[14, 48], [42, 48], [38, 40], [18, 40]]), 0.3);
    fill(ctx, '#5a5a6a', () => rr(ctx, 20, 26, 16, 16, 3), 0.35);
    fill(ctx, '#5a5a6a', () => P.poly(ctx, [[26, 30], [48, 22], [48, 27], [26, 35]]), 0.35);
    fill(ctx, c, () => P.circle(ctx, 47, 24, 3.5), 0.9);
    fill(ctx, c, () => P.circle(ctx, CX, 34, 2.6), 0.9);
  },
  snare_trap: (ctx, c) => {
    stroke(ctx, c, 1.6, () => P.circle(ctx, CX, CY, 20), 0.25);
    fill(ctx, '#8a8a9a', () => P.poly(ctx, [[10, 30], [16, 18], [22, 30], [28, 18], [34, 30], [40, 18], [46, 30], [46, 36], [10, 36]]), 0.35);
    fill(ctx, c, () => P.circle(ctx, CX, 42, 4), 0.9);
  },
  rallying_chord: (ctx, c) => {
    stroke(ctx, c, 2, () => {
      ctx.beginPath();
      ctx.arc(CX, CY, 20, -0.9, 0.9);
      ctx.moveTo(CX + Math.cos(2.2) * 20, CY + Math.sin(2.2) * 20);
      ctx.arc(CX, CY, 20, 2.2, 4.1);
    }, 0.3);
    fill(ctx, c, () => P.circle(ctx, 22, 36, 5.5), 0.8);
    fill(ctx, c, () => P.circle(ctx, 36, 32, 5.5), 0.8);
    stroke(ctx, c, 3, () => P.line(ctx, [[27, 35], [27, 14], [41, 11], [41, 31]]), 0.8);
  },
  // traits
  iron_skin: (ctx, c) => {
    fill(ctx, c, () => P.shield(ctx, CX, CY, 17), 0.5);
    for (const [x, y] of [
      [20, 20],
      [36, 20],
      [28, 28],
      [22, 34],
      [34, 34],
    ])
      dark(ctx, () => P.circle(ctx, x, y, 1.6));
  },
  glass_heart: (ctx, c) => {
    fill(ctx, c, () => P.heart(ctx, CX, 27, 17), 0.8);
    dark(ctx, () => P.poly(ctx, [[27, 12], [30, 22], [25, 27], [30, 36], [28, 44], [27, 44], [27, 36], [23, 27], [28, 22]]));
  },
  swift: (ctx, c) => {
    fill(ctx, c, () => P.wing(ctx, 22, 30, 17, 1), 0.7);
    stroke(ctx, c, 2, () => P.line(ctx, [[8, 24], [17, 24]]), 0.4);
    stroke(ctx, c, 2, () => P.line(ctx, [[6, 32], [16, 32]]), 0.4);
    stroke(ctx, c, 2, () => P.line(ctx, [[9, 40], [18, 40]]), 0.4);
  },
  bloodlust: (ctx, c) => {
    fill(ctx, c, () => P.drop(ctx, 24, 27, 15));
    fill(ctx, c, () => P.drop(ctx, 38, 34, 8));
  },
  pyromancer: (ctx, c) => {
    fill(ctx, c, () => P.flame(ctx, CX, 27, 15), 0.75);
    fill(ctx, c, () => P.circle(ctx, 14, 40, 2.2), 0.8);
    fill(ctx, c, () => P.circle(ctx, 44, 36, 1.8), 0.8);
    fill(ctx, c, () => P.circle(ctx, 40, 46, 1.4), 0.8);
  },
  cryomancer: (ctx, c) => snowflake(ctx, c, CX, CY, 17, 2),
  stormcaller: (ctx, c) => {
    fill(ctx, c, () => P.cloud(ctx, CX, 18, 11), 0.6);
    fill(ctx, c, () => P.bolt(ctx, CX, 36, 12), 0.9);
  },
  plague_doctor: (ctx, c) => {
    fill(ctx, c, () => P.skull(ctx, CX, 27, 12), 0.7);
    P.skullDetails(ctx, CX, 27, 12);
    fill(ctx, c, () => P.circle(ctx, 12, 20, 2.5), 0.8);
    fill(ctx, c, () => P.circle(ctx, 45, 26, 2), 0.8);
    fill(ctx, c, () => P.circle(ctx, 42, 14, 1.6), 0.8);
  },
  thorns: (ctx, c) => {
    for (let i = 0; i < 8; i++) {
      const a = (i * Math.PI * 2) / 8;
      fill(ctx, c, () => P.poly(ctx, [[CX + Math.cos(a) * 22, CY + Math.sin(a) * 22], [CX + Math.cos(a + 0.35) * 12, CY + Math.sin(a + 0.35) * 12], [CX + Math.cos(a - 0.35) * 12, CY + Math.sin(a - 0.35) * 12]]), 0.6);
    }
    stroke(ctx, c, 3, () => P.circle(ctx, CX, CY, 11), 0.5);
  },
  shepherd: (ctx, c) => {
    stroke(ctx, c, 3.2, () => {
      ctx.beginPath();
      ctx.moveTo(26, 50);
      ctx.lineTo(26, 18);
      ctx.arc(31, 18, 5, Math.PI, Math.PI * 2.1);
    });
  },
  zealot: (ctx, c) => {
    rays(ctx, c, CX, CY, 13, 22, 8, 2.2);
    fill(ctx, c, () => P.circle(ctx, CX, CY, 9), 0.85);
  },
  marksman: (ctx, c) => {
    stroke(ctx, c, 2, () => P.circle(ctx, CX, CY, 16), 0.5);
    stroke(ctx, c, 2, () => {
      ctx.beginPath();
      for (const [dx, dy] of [
        [0, -1],
        [0, 1],
        [1, 0],
        [-1, 0],
      ]) {
        ctx.moveTo(CX + dx * 9, CY + dy * 9);
        ctx.lineTo(CX + dx * 22, CY + dy * 22);
      }
    });
    fill(ctx, c, () => P.circle(ctx, CX, CY, 3), 0.9);
  },
  vampiric: (ctx, c) => {
    fill(ctx, c, () => P.wing(ctx, 26, 28, 15, -1), 0.5);
    fill(ctx, c, () => P.wing(ctx, 30, 28, 15, 1), 0.5);
    fill(ctx, c, () => P.circle(ctx, CX, 30, 6), 0.6);
    light(ctx, () => P.poly(ctx, [[25, 34], [26, 40], [27, 34]]));
    light(ctx, () => P.poly(ctx, [[29, 34], [30, 40], [31, 34]]));
  },
  alchemist: (ctx, c) => {
    fill(ctx, c, () => P.flask(ctx, CX, 29, 16), 0.55);
    light(ctx, () => P.circle(ctx, 24, 36, 2), 0.6);
    light(ctx, () => P.circle(ctx, 31, 40, 1.5), 0.6);
    light(ctx, () => P.circle(ctx, 30, 31, 1.2), 0.6);
  },
  momentum: (ctx, c) => {
    stroke(ctx, c, 2, () => P.line(ctx, [[8, 22], [18, 22]]), 0.3);
    stroke(ctx, c, 2, () => P.line(ctx, [[6, 30], [16, 30]]), 0.3);
    stroke(ctx, c, 2, () => P.line(ctx, [[9, 38], [17, 38]]), 0.3);
    arrow(ctx, c, 20, 30, 50, 30, 3.2);
  },
  warlock: (ctx, c) => {
    stroke(ctx, c, 2, () => P.circle(ctx, CX, CY, 19), 0.35);
    fill(ctx, c, () => P.star(ctx, CX, CY, 17, 5, 0.42), 0.6);
  },
  bulwark_trait: (ctx, c) => {
    fill(ctx, c, () => rr(ctx, 15, 8, 26, 40, 6), 0.5);
    stroke(ctx, c, 2.4, () => P.line(ctx, [[CX, 16], [CX, 40]]), 0.75);
    stroke(ctx, c, 2.4, () => P.line(ctx, [[20, 24], [36, 24]]), 0.75);
  },
  exploder: (ctx, c) => {
    fill(ctx, c, () => P.star(ctx, CX, CY, 21, 9, 0.5), 0.75);
    light(ctx, () => P.circle(ctx, CX, CY, 4), 0.8);
  },
  executioner: (ctx, c) => {
    stroke(ctx, '#6a4a2c', 3.4, () => P.line(ctx, [[18, 48], [36, 14]]), 0.3);
    fill(ctx, c, () => {
      ctx.beginPath();
      ctx.moveTo(34, 10);
      ctx.quadraticCurveTo(50, 12, 46, 30);
      ctx.lineTo(38, 22);
      ctx.closePath();
    }, 0.6);
  },
};

const KEYSTONE_ICONS: Record<string, Painter> = {
  glass_cannon: (ctx, c) => {
    fill(ctx, c, () => P.gem(ctx, CX, 27, 16), 0.85);
    dark(ctx, () => P.line(ctx, [[22, 20], [27, 30], [24, 40]]), alpha('#000000', 0));
    stroke(ctx, '#0e0b14', 1.4, () => P.line(ctx, [[22, 20], [27, 30], [24, 42]]), 0);
    stroke(ctx, '#0e0b14', 1.2, () => P.line(ctx, [[27, 30], [34, 26]]), 0);
  },
  mirror: (ctx, c) => {
    fill(ctx, c, () => rr(ctx, 16, 9, 24, 38, 5), 0.85);
    light(ctx, () => P.poly(ctx, [[20, 40], [33, 13], [37, 13], [24, 40]]), 0.55);
  },
  legion: (ctx, c) => {
    fill(ctx, c, () => P.figure(ctx, 16, 32, 9), 0.5);
    fill(ctx, c, () => P.figure(ctx, 40, 32, 9), 0.5);
    fill(ctx, c, () => P.figure(ctx, CX, 29, 11), 0.8);
  },
  lone_wolf: (ctx, c) => {
    fill(ctx, c, () => P.crescent(ctx, 22, 22, 13), 0.85);
    fill(ctx, c, () => P.circle(ctx, 36, 40, 5), 0.6);
    for (const [x, y] of [
      [30, 33],
      [35, 31],
      [41, 33],
    ])
      fill(ctx, c, () => P.circle(ctx, x, y, 2.2), 0.6);
  },
  blood_for_blood: (ctx, c) => {
    fill(ctx, c, () => P.drop(ctx, 21, 27, 13));
    fill(ctx, c, () => P.drop(ctx, 36, 30, 11));
  },
  frozen_heart: (ctx, c) => {
    fill(ctx, '#ff5c7a', () => P.heart(ctx, CX, 28, 16), 0.5);
    snowflake(ctx, c, CX, 27, 10, 1.6);
  },
  ash: (ctx, c) => {
    fill(ctx, '#4a4048', () => {
      ctx.beginPath();
      ctx.ellipse(CX, 44, 17, 5, 0, 0, Math.PI * 2);
    }, 0.3);
    fill(ctx, c, () => P.flame(ctx, CX, 27, 15), 0.7);
    for (const [x, y] of [
      [14, 18],
      [42, 22],
      [40, 12],
    ])
      dark(ctx, () => P.circle(ctx, x, y, 1.6), '#8a8090');
  },
  steadfast: (ctx, c) => {
    stroke(ctx, c, 3, () => P.circle(ctx, CX, 12, 4), 0.6);
    stroke(ctx, c, 3.2, () => P.line(ctx, [[CX, 16], [CX, 46]]), 0.6);
    stroke(ctx, c, 3.2, () => P.line(ctx, [[18, 22], [38, 22]]), 0.6);
    stroke(ctx, c, 3.2, () => {
      ctx.beginPath();
      ctx.arc(CX, 32, 14, Math.PI * 0.15, Math.PI * 0.85);
    }, 0.6);
  },
  shepherd_of_light: (ctx, c) => {
    rays(ctx, c, 31, 18, 8, 14, 6, 1.6);
    stroke(ctx, c, 3.2, () => {
      ctx.beginPath();
      ctx.moveTo(24, 50);
      ctx.lineTo(24, 20);
      ctx.arc(29, 20, 5, Math.PI, Math.PI * 2.1);
    }, 0.7);
  },
  necromancer: (ctx, c) => {
    fill(ctx, c, () => P.skull(ctx, CX, 31, 12), 0.7);
    P.skullDetails(ctx, CX, 31, 12);
    fill(ctx, '#ffd166', () => P.crown(ctx, CX, 15, 9), 0.5);
  },
  marauder: (ctx, c) => {
    sword(ctx, c, CX, CY, 17, -0.7);
    sword(ctx, c, CX, CY, 17, 0.7);
  },
  acceleration: (ctx, c) => {
    for (let i = 0; i < 3; i++) {
      const x = 14 + i * 11;
      stroke(ctx, c, 3, () => P.line(ctx, [[x, 15], [x + 9, CY], [x, 41]]), 0.3 + i * 0.25);
    }
  },
};

const ITEM_ICONS: Record<string, Painter> = {
  claymore: (ctx, c) => sword(ctx, c, CX, CY, 21),
  longbow: (ctx, c) => {
    bow(ctx, c, 26, CY, 21);
    arrow(ctx, '#e8dcc0', 8, CY, 46, CY, 1.8);
  },
  staff: (ctx, c) => staff(ctx, c, 18, 50, 36, 14, 6),
  crossbow: (ctx, c) => {
    bow(ctx, c, CX, 20, 18, Math.PI / 2);
    fill(ctx, '#6a4a2c', () => P.poly(ctx, [[25, 16], [31, 16], [30, 50], [26, 50]]), 0.3);
    arrow(ctx, '#e8dcc0', CX, 40, CX, 8, 1.6);
  },
  flail: (ctx, c) => {
    stroke(ctx, '#6a4a2c', 3.4, () => P.line(ctx, [[14, 50], [26, 30]]), 0.3);
    for (let i = 0; i < 4; i++) fill(ctx, c, () => P.circle(ctx, 27 + i * 3, 28 - i * 3.5, 1.8), 0.5);
    fill(ctx, c, () => P.star(ctx, 42, 14, 10, 8, 0.72), 0.6);
  },
  fire_wand: (ctx, c) => {
    stroke(ctx, '#6a4a2c', 3, () => P.line(ctx, [[16, 50], [32, 28]]), 0.3);
    fill(ctx, c, () => P.flame(ctx, 37, 19, 11), 0.75);
  },
  twin_daggers: (ctx, c) => {
    sword(ctx, c, 21, 30, 13, -0.35);
    sword(ctx, c, 35, 30, 13, 0.35);
  },
  frost_staff: (ctx, c) => {
    staff(ctx, c, 18, 50, 36, 16, 0);
    snowflake(ctx, c, 37, 15, 9, 1.8);
  },
  storm_bow: (ctx, c) => {
    bow(ctx, c, 26, CY, 21);
    fill(ctx, c, () => P.bolt(ctx, 36, CY, 11), 0.9);
  },
  doom_blade: (ctx, c) => {
    sword(ctx, c, CX, CY, 21, 0, true);
    stroke(ctx, c, 1.4, () => P.line(ctx, [[CX, 8], [CX, 32]]), 0.6);
  },
  halberd: (ctx, c) => {
    stroke(ctx, '#6a4a2c', 3.2, () => P.line(ctx, [[20, 50], [34, 10]]), 0.3);
    fill(ctx, c, () => P.poly(ctx, [[34, 10], [36, 4], [38, 12]]), 0.8);
    fill(ctx, c, () => {
      ctx.beginPath();
      ctx.moveTo(35, 12);
      ctx.quadraticCurveTo(50, 12, 48, 28);
      ctx.lineTo(38, 22);
      ctx.closePath();
    }, 0.75);
    fill(ctx, c, () => P.poly(ctx, [[33, 14], [24, 18], [32, 21]]), 0.6);
  },
  soul_orb: (ctx, c) => {
    fill(ctx, c, () => P.circle(ctx, CX, CY, 15), 0.6);
    stroke(ctx, c, 2.2, () => P.spiral(ctx, CX, CY, 11, 1.6), 0.8);
    light(ctx, () => P.circle(ctx, 22, 21, 3), 0.5);
  },
  claws: (ctx, c) => {
    for (let i = 0; i < 3; i++) {
      const x = 16 + i * 12;
      fill(ctx, c, () => {
        ctx.beginPath();
        ctx.moveTo(x - 4, 12);
        ctx.quadraticCurveTo(x + 10, 24, x + 2, 46);
        ctx.quadraticCurveTo(x - 2, 30, x - 8, 16);
        ctx.closePath();
      }, 0.8);
    }
  },
  sickle: (ctx, c) => {
    stroke(ctx, '#6a4a2c', 3.4, () => P.line(ctx, [[22, 50], [22, 26]]), 0.3);
    fill(ctx, c, () => {
      ctx.beginPath();
      ctx.arc(30, 22, 16, Math.PI * 0.95, Math.PI * 2.05);
      ctx.arc(30, 22, 10, Math.PI * 2.05, Math.PI * 0.95, true);
      ctx.closePath();
    }, 0.8);
  },
  totem_staff: (ctx, c) => {
    staff(ctx, c, 20, 50, 34, 16, 0);
    fill(ctx, '#7a5a3a', () => rr(ctx, 29, 8, 10, 14, 2), 0.35);
    dark(ctx, () => P.circle(ctx, 32, 13, 1.4));
    dark(ctx, () => P.circle(ctx, 36, 13, 1.4));
    stroke(ctx, c, 1.8, () => P.line(ctx, [[24, 10], [20, 4]]), 0.6);
    stroke(ctx, '#e8dcc0', 1.8, () => P.line(ctx, [[27, 9], [25, 2]]), 0.6);
  },
  sparkgun: (ctx, c) => {
    fill(ctx, '#5a5a6a', () => P.poly(ctx, [[12, 24], [44, 24], [44, 32], [12, 32]]), 0.35);
    fill(ctx, '#7a6a4a', () => P.poly(ctx, [[16, 32], [26, 32], [24, 46], [16, 46]]), 0.35);
    fill(ctx, '#3a3542', () => rr(ctx, 22, 18, 12, 8, 2), 0.3);
    fill(ctx, c, () => P.circle(ctx, 46, 28, 4), 0.9);
    rays(ctx, c, 46, 28, 6, 10, 5, 1.4);
  },
  lute: (ctx, c) => {
    fill(ctx, '#5a4a3a', () => P.poly(ctx, [[26, 4], [30, 4], [31, 30], [25, 30]]), 0.3);
    fill(ctx, c, () => {
      ctx.beginPath();
      ctx.ellipse(CX, 38, 12, 14, 0, 0, Math.PI * 2);
    }, 0.55);
    dark(ctx, () => P.circle(ctx, CX, 36, 3.5));
    for (let i = -1; i <= 1; i++) stroke(ctx, '#fff2c0', 0.8, () => P.line(ctx, [[CX + i * 1.5, 4], [CX + i * 2.5, 48]]), 0);
  },
  chain_mail: (ctx, c) => {
    fill(ctx, c, () => P.torso(ctx, CX, CY, 17), 0.45);
    ctx.save();
    ctx.strokeStyle = 'rgba(10,8,14,0.6)';
    ctx.lineWidth = 1;
    for (let y = 18; y <= 42; y += 5) for (let x = 18 + ((y / 5) % 2) * 2.5; x <= 38; x += 5) {
      ctx.beginPath();
      ctx.arc(x, y, 2, 0, Math.PI * 2);
      ctx.stroke();
    }
    ctx.restore();
  },
  leather: (ctx, c) => {
    fill(ctx, c, () => P.torso(ctx, CX, CY, 17), 0.4);
    stroke(ctx, '#0e0b14', 1.2, () => P.line(ctx, [[CX, 18], [CX, 44]]), 0);
    stroke(ctx, '#0e0b14', 1.2, () => P.line(ctx, [[20, 24], [36, 24]]), 0);
  },
  robe: (ctx, c) => {
    fill(ctx, c, () => P.poly(ctx, [[20, 10], [36, 10], [40, 20], [44, 50], [12, 50], [16, 20]]), 0.45);
    dark(ctx, () => P.poly(ctx, [[24, 10], [32, 10], [CX, 22]]), alpha('#000000', 0.45));
    stroke(ctx, c, 1.4, () => P.line(ctx, [[CX, 24], [CX, 48]]), 0.7);
  },
  plate: (ctx, c) => {
    fill(ctx, c, () => P.torso(ctx, CX, CY, 17), 0.75);
    fill(ctx, c, () => P.circle(ctx, 12, 16, 6), 0.6);
    fill(ctx, c, () => P.circle(ctx, 44, 16, 6), 0.6);
    stroke(ctx, '#0e0b14', 1.4, () => P.line(ctx, [[CX, 16], [CX, 44]]), 0);
  },
  mirror_plate: (ctx, c) => {
    fill(ctx, c, () => P.torso(ctx, CX, CY, 17), 0.9);
    light(ctx, () => P.poly(ctx, [[20, 42], [33, 16], [37, 16], [24, 42]]), 0.5);
  },
  bone_armor: (ctx, c) => {
    stroke(ctx, c, 3, () => P.line(ctx, [[CX, 10], [CX, 48]]), 0.55);
    for (let i = 0; i < 4; i++) {
      const y = 16 + i * 9;
      stroke(ctx, c, 2.4, () => {
        ctx.beginPath();
        ctx.moveTo(CX, y);
        ctx.quadraticCurveTo(14, y + 2, 15, y + 8);
        ctx.moveTo(CX, y);
        ctx.quadraticCurveTo(42, y + 2, 41, y + 8);
      }, 0.55);
    }
  },
  ashen_mantle: (ctx, c) => {
    fill(ctx, c, () => {
      ctx.beginPath();
      ctx.moveTo(CX, 8);
      ctx.quadraticCurveTo(40, 10, 42, 24);
      ctx.lineTo(46, 50);
      ctx.lineTo(10, 50);
      ctx.lineTo(14, 24);
      ctx.quadraticCurveTo(16, 10, CX, 8);
      ctx.closePath();
    }, 0.35);
    dark(ctx, () => P.poly(ctx, [[22, 14], [34, 14], [CX, 26]]), alpha('#000000', 0.5));
    for (const [x, y] of [
      [18, 40],
      [34, 44],
      [38, 34],
      [24, 47],
    ])
      fill(ctx, '#ff8c42', () => P.circle(ctx, x, y, 1.6), 0.6);
  },
  amulet_regen: (ctx, c) => pendant(ctx, c, (x, y) => light(ctx, () => P.heart(ctx, x, y, 4.5), 0.9)),
  amulet_crit: (ctx, c) => pendant(ctx, c, (x, y) => light(ctx, () => P.star(ctx, x, y + 1, 5.5, 5, 0.45), 0.9)),
  amulet_greed: (ctx, c) => pendant(ctx, c, (x, y) => {
    light(ctx, () => P.circle(ctx, x, y + 1, 5), 0.9);
    dark(ctx, () => P.circle(ctx, x, y + 1, 2.4), alpha(c, 0.9));
  }),
  amulet_haste: (ctx, c) => pendant(ctx, c, (x, y) => light(ctx, () => P.wing(ctx, x - 3, y + 1, 6, 1), 0.9)),
  amulet_wrath: (ctx, c) => pendant(ctx, c, (x, y) => light(ctx, () => P.flame(ctx, x, y + 1, 6), 0.9)),
  amulet_summons: (ctx, c) => pendant(ctx, c, (x, y) => light(ctx, () => P.skull(ctx, x, y + 1, 4.5), 0.9)),
  amulet_storm: (ctx, c) => pendant(ctx, c, (x, y) => light(ctx, () => P.bolt(ctx, x, y + 1, 6), 0.9)),
  amulet_lich: (ctx, c) => pendant(ctx, c, (x, y) => {
    light(ctx, () => P.skull(ctx, x, y + 2, 4), 0.9);
    light(ctx, () => P.crown(ctx, x, y - 4, 3.5), 0.9);
  }),
};

// legendaries wear the silhouette of their base item plus a mark of their own
Object.assign(ITEM_ICONS, {
  sunspear: (ctx: Ctx, c: string) => {
    ITEM_ICONS.longbow(ctx, c);
    rays(ctx, c, 46, 12, 4, 9, 6, 1.4);
  },
  grave_edge: (ctx: Ctx, c: string) => {
    ITEM_ICONS.doom_blade(ctx, c);
    light(ctx, () => P.skull(ctx, 14, 14, 4), 0.8);
  },
  frostbrand: (ctx: Ctx, c: string) => {
    ITEM_ICONS.frost_staff(ctx, c);
    snowflake(ctx, '#ffffff', 14, 14, 5, 1.2);
  },
  stormheart: (ctx: Ctx, c: string) => {
    ITEM_ICONS.storm_bow(ctx, c);
    fill(ctx, c, () => P.bolt(ctx, 14, 14, 6), 0.9);
  },
  heart_of_oak: (ctx: Ctx, c: string) => {
    ITEM_ICONS.leather(ctx, c);
    light(ctx, () => P.heart(ctx, CX, 30, 6), 0.85);
  },
  mirror_carapace: (ctx: Ctx, c: string) => {
    ITEM_ICONS.mirror_plate(ctx, c);
    rays(ctx, '#ffffff', 44, 12, 3, 7, 5, 1.2);
  },
  shroud_of_night: (ctx: Ctx, c: string) => {
    ITEM_ICONS.robe(ctx, c);
    light(ctx, () => P.circle(ctx, 42, 14, 4), 0.8);
    dark(ctx, () => P.circle(ctx, 44, 13, 3.4), '#14101c');
  },
  crown_of_ash: (ctx: Ctx, c: string) => pendant(ctx, c, (x, y) => light(ctx, () => P.crown(ctx, x, y, 5), 0.9)),
  idol_of_legion: (ctx: Ctx, c: string) =>
    pendant(ctx, c, (x, y) => {
      light(ctx, () => P.skull(ctx, x - 4, y + 1, 3), 0.9);
      light(ctx, () => P.skull(ctx, x + 4, y + 1, 3), 0.9);
    }),
  gambler_coin: (ctx: Ctx, c: string) =>
    pendant(ctx, c, (x, y) => {
      light(ctx, () => P.circle(ctx, x, y + 1, 5.5), 0.9);
      dark(ctx, () => P.star(ctx, x, y + 1, 3.2, 5, 0.5), alpha(c, 0.9));
    }),
  wailing_horn: (ctx: Ctx, c: string) => pendant(ctx, c, (x, y) => light(ctx, () => P.poly(ctx, [[x - 6, y + 4], [x + 6, y - 4], [x + 6, y + 2], [x - 4, y + 6]]), 0.9)),
} satisfies Record<string, Painter>);

const CONS_ICONS: Record<string, Painter> = {
  potion_life: (ctx, c) => {
    fill(ctx, c, () => P.flask(ctx, CX, 29, 16), 0.5);
    light(ctx, () => P.heart(ctx, CX, 34, 5), 0.9);
  },
  elixir_haste: (ctx, c) => {
    fill(ctx, c, () => P.flask(ctx, CX, 29, 16), 0.5);
    for (let i = 0; i < 2; i++) light(ctx, () => P.poly(ctx, [[22, 40 - i * 6], [CX, 34 - i * 6], [34, 40 - i * 6], [34, 43 - i * 6], [CX, 37 - i * 6], [22, 43 - i * 6]]), 0.9);
  },
  scroll_choice: (ctx, c) => {
    fill(ctx, c, () => rr(ctx, 14, 12, 28, 32, 3), 0.55);
    fill(ctx, c, () => {
      ctx.beginPath();
      ctx.ellipse(CX, 12, 15, 4, 0, 0, Math.PI * 2);
    }, 0.75);
    fill(ctx, c, () => {
      ctx.beginPath();
      ctx.ellipse(CX, 44, 15, 4, 0, 0, Math.PI * 2);
    }, 0.75);
    for (let i = 0; i < 3; i++) stroke(ctx, '#0e0b14', 1.3, () => P.line(ctx, [[19, 22 + i * 7], [37, 22 + i * 7]]), 0);
  },
  bomb: (ctx, c) => {
    stroke(ctx, '#6a4a2c', 2.4, () => {
      ctx.beginPath();
      ctx.moveTo(32, 18);
      ctx.quadraticCurveTo(38, 8, 44, 12);
    }, 0.3);
    fill(ctx, '#ffe38a', () => P.star(ctx, 45, 11, 4.5, 5, 0.45), 0.6);
    fill(ctx, '#3a3542', () => P.circle(ctx, CX, 32, 15), 0.35);
    fill(ctx, c, () => P.poly(ctx, [[25, 14], [33, 14], [34, 19], [24, 19]]), 0.5);
    light(ctx, () => P.circle(ctx, 22, 27, 3), 0.35);
  },
  ward: (ctx, c) => {
    fill(ctx, c, () => P.shield(ctx, CX, CY, 17), 0.5);
    light(ctx, () => P.star(ctx, CX, CY - 1, 8, 4, 0.5), 0.9);
  },
};

// ------------------------------------------------------------------ public API

function render(key: string, color: string, painter: Painter): string {
  const hit = cache.get(key);
  if (hit) return hit;
  const canvas = document.createElement('canvas');
  canvas.width = SIZE * RES;
  canvas.height = SIZE * RES;
  const ctx = canvas.getContext('2d')!;
  ctx.scale(RES, RES);
  plate(ctx, color);
  painter(ctx, color);
  const url = canvas.toDataURL();
  cache.set(key, url);
  return url;
}

/** Data URL of the icon for a content id, or null when no drawing exists (the glyph is used). */
export function iconUrl(kind: 'skill' | 'keystone' | 'item' | 'cons' | 'aspect' | 'blessing' | 'crowd', id: string, skill?: string): string | null {
  if (kind === 'blessing' || kind === 'crowd') return null;
  if (kind === 'skill') {
    const def = SKILLS[id];
    const p = SKILL_ICONS[id];
    return def && p ? render('skill:' + id, def.color, p) : null;
  }
  if (kind === 'aspect') {
    const parent = skill ? SKILLS[skill] : undefined;
    const asp = skill ? aspectOf(skill, id) : undefined;
    const p = skill ? SKILL_ICONS[skill] : undefined;
    if (!parent || !p) return null;
    const color = asp?.color ?? parent.color;
    return render('aspect:' + skill + ':' + id, color, (ctx, c) => {
      p(ctx, c);
      // aspect mark: a small facet in the corner
      fill(ctx, c, () => P.gem(ctx, 47, 46, 5), 0.9);
    });
  }
  if (kind === 'keystone') {
    const def = KEYSTONES[id];
    const p = KEYSTONE_ICONS[id];
    return def && p
      ? render('keystone:' + id, def.color, (ctx, c) => {
          diamondFrame(ctx, c);
          p(ctx, c);
        })
      : null;
  }
  if (kind === 'item') {
    const def = ITEMS[id];
    const p = ITEM_ICONS[id];
    return def && p ? render('item:' + id, def.color, p) : null;
  }
  const def = CONSUMABLES[id];
  const p = CONS_ICONS[id];
  return def && p ? render('cons:' + id, def.color, p) : null;
}
